import { useState, useEffect, useRef } from "react";
import { useCompleteTask, useDeleteTask, useTaskEventMappings, useTaskFusionCalendar, useCreateEventFromTask, useCreateTaskFusionCalendar, useDeleteCalendarEvent, useDeleteTaskEventMapping, useVerifyCalendarEvent, useExplosionAnimation } from "@/hooks";
import { useSearchHighlight } from "@/hooks/useSearchHighlight";
import useUIStore from "@/stores/useUIStore";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { tasks_v1 } from "@googleapis/tasks";
import { Circle, Check, Trash, MoreVertical, ExternalLink, Loader2, ArrowRight } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import MoveTaskDialog from "../MoveTaskDialog";
import { truncateString } from "@/helpers";
import { TaskItemProps } from "./types";
import { toast } from "sonner";
import { InsufficientScopesError } from "@/api/axios";
import ScopeRequestDialog from "../Calendar/ScopeRequestDialog";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileActionSheet, ActionSheetItem, ActionSheetSeparator } from "@/components/MobileActionSheet";
import "./styles/TaskItem.css";

// Helper functions
const convertToUTCString = (date: string) =>
  new Date(date).toUTCString().split(" ").slice(0, 4).join(" ");

const linkifyUrls = (text: string) => {
  const urlPattern = /(?:(?:https?:\/\/)?(?:www\.)?(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s)*<>"]*)?)/g;
  
  return text.replace(urlPattern, (url) => {
    const fullUrl = url.startsWith('http') ? url : `https://${url}`;
    return `<a href="${fullUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:underline">${url}</a>`;
  });
};

const TaskItem = ({ task, list, account, variant, onEdit }: TaskItemProps) => {
  const completeTaskMutation = useCompleteTask(account, list);
  const deleteTaskMutation = useDeleteTask();
  const isMobile = useIsMobile();
  const [actionsSheetOpen, setActionsSheetOpen] = useState(false);
  const [moveTaskModalOpen, setMoveTaskModalOpen] = useState(false);
  const [showCalendarPrompt, setShowCalendarPrompt] = useState(false);
  const [scopeError, setScopeError] = useState<{ userId: string; retryFn?: () => void } | null>(null);
  const { isAnimating, triggerAnimation, resetAnimation } = useExplosionAnimation({
    delay: 200,
  });

  const titleRef = useRef<HTMLParagraphElement>(null);
  const search = useUIStore((state) => state.filters.search);
  useSearchHighlight(titleRef, search);
  
  const { data: mappings } = useTaskEventMappings(account.id, account.id);
  const { data: calendar } = useTaskFusionCalendar(account.id, account.id);
  const createCalendarMutation = useCreateTaskFusionCalendar();
  const createEventMutation = useCreateEventFromTask();
  const deleteCalendarEventMutation = useDeleteCalendarEvent();
  const deleteMappingMutation = useDeleteTaskEventMapping();
  const cleanupTriggeredRef = useRef<string | null>(null);
  
  const taskMapping = task.id && mappings ? mappings[task.id] : null;
  
  // Verify event exists if mapping exists
  const { data: verifiedEvent, isError: verificationError, isLoading: isVerifying } = useVerifyCalendarEvent(
    account.id,
    taskMapping?.calendarId || '',
    taskMapping?.eventId || '',
    account.id,
    !!taskMapping && !!taskMapping.calendarId && !!taskMapping.eventId
  );
  
  // Reset cleanup trigger when mapping changes or is removed
  useEffect(() => {
    if (!taskMapping) {
      cleanupTriggeredRef.current = null;
    }
  }, [taskMapping]);
  
  // Clean up orphaned mapping if event doesn't exist
  useEffect(() => {
    if (taskMapping && task.id && !isVerifying && cleanupTriggeredRef.current !== task.id) {
      // Only clean up if verification completed and event doesn't exist (null)
      // Don't clean up on network errors - only on confirmed 404
      if (verifiedEvent === null && !verificationError) {
        cleanupTriggeredRef.current = task.id;
        deleteMappingMutation.mutate({
          accountId: account.id,
          taskId: task.id,
          userId: account.id,
        });
      }
    }
  }, [taskMapping, verifiedEvent, verificationError, isVerifying, task.id, account.id, deleteMappingMutation]);

  // Check if task is due
  const yesterdayDate = new Date();
  yesterdayDate.setDate(new Date().getDate() - 1);
  const isDue = yesterdayDate > new Date(task.due || "");



  // Handle task completion toggle
  const handleToggleComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!task.id || isAnimating) return;
    
    const apiTask: Partial<tasks_v1.Schema$Task> = {
      ...task,
      position: String(task.position),
      status: variant === "needsAction" ? "completed" : "needsAction"
    };

    if (typeof task.id !== 'string') return;
    
    triggerAnimation(() => {
      completeTaskMutation.mutate({
        task: task.id || '', // TODO: look into why guard clause is not catching this
        updatedFields: apiTask,
      }, {
        onError: () => {
          resetAnimation();
        },
      });
    });
  };

  // Handle task deletion - always cleanup calendar event if mapping exists
  const handleDeleteTask = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!task.id) return;

    // Delete task immediately (optimistic update happens right away)
    deleteTaskMutation.mutate({
      account,
      tasklist: list,
      task: task.id,
    });

    // Cleanup calendar event and mapping in the background (if they exist)
    if (taskMapping) {
      // Delete calendar event (optimistic update happens immediately)
      deleteCalendarEventMutation.mutate({
        accountId: account.id,
        calendarId: taskMapping.calendarId,
        eventId: taskMapping.eventId,
        userId: account.id,
      }, {
        onError: (error) => {
          // Log but continue - event might already be deleted
          console.error('Error deleting calendar event:', error);
        }
      });
      
      // Delete mapping (optimistic update happens immediately)
      deleteMappingMutation.mutate({
        accountId: account.id,
        taskId: task.id,
        userId: account.id,
      }, {
        onError: (error) => {
          // Log but continue - mapping might already be deleted
          console.error('Error deleting mapping:', error);
        }
      });
    }
  };

  const handleAddToCalendar = async () => {
    if (!task.id) return;
    
    if (createEventMutation.isPending) return;

    const calendarId = calendar?.id;
    
    if (!calendarId) {
      if (!calendar) {
        setShowCalendarPrompt(true);
        return;
      }
      return;
    }

    // Show loading toast and get its ID
    const toastId = toast.loading("Adding to calendar...");

    try {
      await createEventMutation.mutateAsync({
        accountId: account.id,
        calendarId,
        task,
        userId: account.id,
      });
      
      // Update to success
      toast.success("Task added to calendar", { id: toastId });
    } catch (error) {
      // Dismiss the loading toast
      toast.dismiss(toastId);
      
      if (error instanceof InsufficientScopesError) {
        // Don't show error toast for scope errors - show dialog instead
        setScopeError({
          userId: error.userId,
          retryFn: () => handleAddToCalendar(),
        });
        return;
      }
      
      // Show error toast for other errors
      toast.error("Failed to add task to calendar");
    }
  };

  const handleCreateCalendarAndAdd = async () => {
    // Show loading toast and get its ID
    const toastId = toast.loading("Creating calendar and adding task...");

    try {
      const newCalendar = await createCalendarMutation.mutateAsync({
        accountId: account.id,
        userId: account.id,
      });
      
      await createEventMutation.mutateAsync({
        accountId: account.id,
        calendarId: newCalendar.id,
        task,
        userId: account.id,
      });
      
      setShowCalendarPrompt(false);
      // Update to success
      toast.success("Task added to calendar", {
        id: toastId,
        description: `"${task.title}" has been added to your calendar`,
      });
    } catch (error) {
      // Dismiss the loading toast
      toast.dismiss(toastId);
      
      if (error instanceof InsufficientScopesError) {
        setShowCalendarPrompt(false);
        setScopeError({
          userId: error.userId,
          retryFn: () => handleCreateCalendarAndAdd(),
        });
        return;
      }
      console.error('Error creating calendar and adding event:', error);
      toast.error("Failed to add task to calendar", {
        description: "Please try again later",
      });
    }
  };

  // Handle task edit (when clicking on task content)
  const handleEditTask = (e: React.MouseEvent) => {
    if (!task.id) return;
    
    // Don't trigger edit mode when clicking on links
    if (!(e.target as HTMLElement).closest('a')) {
      // Don't trigger edit mode if clicking inside a dialog
      if ((e.target as HTMLElement).closest('[role="dialog"]')) {
        return;
      }
      onEdit(task.id);
    }
  };

  return (
    <>
      {scopeError && (
        <ScopeRequestDialog
          isOpen={!!scopeError}
          onClose={() => setScopeError(null)}
          userId={scopeError.userId}
          onSuccess={() => {
            if (scopeError.retryFn) {
              scopeError.retryFn();
            }
            setScopeError(null);
          }}
        />
      )}
      {/* Mobile action sheet lives outside the row container so its (portaled)
          clicks don't bubble into handleEditTask */}
      {isMobile && variant === "needsAction" && (
        <MobileActionSheet open={actionsSheetOpen} onOpenChange={setActionsSheetOpen} title={truncateString(task.title || "Task", 40)}>
          <ActionSheetItem
            icon={ArrowRight}
            label="Move Task"
            onClick={() => {
              setActionsSheetOpen(false);
              setMoveTaskModalOpen(true);
            }}
          />
          <ActionSheetSeparator />
          <ActionSheetItem
            icon={Trash}
            label="Delete"
            destructive
            onClick={() => {
              setActionsSheetOpen(false);
              handleDeleteTask();
            }}
          />
        </MobileActionSheet>
      )}
      <div className="list-item-container active:bg-card active:shadow active:border py-1" onClick={handleEditTask}>
      <div className="flex items-center justify-between px-4 mb-1">
        {/* Status toggle button */}
        <div
          className={`hover:cursor-pointer h-5 min-w-5 icon-container ${isAnimating ? 'poof-animation' : ''}`}
          onClick={handleToggleComplete}
        >
          {variant === "needsAction" ? (
            <>
              <Check size="1.2rem" className="icon check-icon text-primary" />
              <Circle size="1.2rem" className="icon circle-icon text-muted-foreground" />
            </>
          ) : (
            <Check size="1.2rem" />
          )}
        </div>

        {/* Task title */}
        <div className="w-full">
          <div
            className={`flex flex-col justify-start border-transparent w-full text-sm ${variant === "completed" && "line-through"} px-2`}
          >
            <p 
              ref={titleRef}
              className="break-words max-h-16 overflow-hidden ph-no-capture"
              dangerouslySetInnerHTML={{ __html: linkifyUrls(task.title || "") }}
            />
          </div>
        </div>

        {/* Action buttons */}
        {variant !== "needsAction" ? (
          <Button
            onClick={handleDeleteTask}
            className="h-8 w-10 p-0 rounded-full hover:bg-card delete-button"
            variant="ghost"
            size="sm"
          >
            <Trash className="h-4 w-4" />
          </Button>
        ) : isMobile ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 w-10 p-0 rounded-full hover:bg-card"
            aria-label="Task actions"
            onClick={(e) => {
              e.stopPropagation();
              setActionsSheetOpen(true);
            }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4 translate-y-[0.1rem]" />
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-10 p-0 rounded-full hover:bg-card task-actions-dropdown"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4 translate-y-[0.1rem]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="dark:bg-background">
              <DropdownMenuItem 
                className="cursor-pointer" 
                onClick={(e) => {
                  e.stopPropagation();
                  setMoveTaskModalOpen(true);
                }}
              >
                <ArrowRight className="h-4 w-4 mr-2" />
                Move Task
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="cursor-pointer text-red-500" 
                onClick={handleDeleteTask}
              >
                <Trash className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Task details - notes and metadata */}
      <div className="mx-4 px-4">
        {task.notes && (
          <p className="text-xs leading-relaxed text-muted-foreground mx-3 mb-2 break-words ph-no-capture">
            {truncateString(task.notes, 50)}
          </p>
        )}
        
        {(task.due || task.assignmentInfo) && (
          <div className="flex items-center flex-wrap">
            {task.due && (
              <Badge
                className={
                  isDue
                    ? "ml-2 w-fit mb-2 border-transparent bg-red-100 text-red-700 hover:bg-red-100/80 dark:border-transparent dark:bg-red-500/15 dark:text-red-300 dark:hover:bg-red-500/10"
                    : "ml-2 w-fit mb-2 border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100/80 dark:border-transparent dark:bg-blue-500/15 dark:text-blue-300 dark:hover:bg-blue-500/10"
                }
                variant="outline"
              >
                {convertToUTCString(task.due)}
              </Badge>
            )}
            
            {task.assignmentInfo && (
              <a 
                href={task.assignmentInfo.linkToTask || ""}
                target="_blank" 
                rel="noopener noreferrer" 
                className="hover:cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <Badge className="ml-1 w-fit mb-2" variant="outline">
                  <ExternalLink className="h-4 w-4 mr-1" />Go to Task
                </Badge>
              </a>
            )}
          </div>
        )}
      </div>

      {/* Move task dialog */}
      <MoveTaskDialog 
        open={moveTaskModalOpen} 
        onOpenChange={setMoveTaskModalOpen} 
        task={task} 
        list={list} 
        account={account} 
      />

      {/* Calendar creation prompt */}
      <Dialog 
        open={showCalendarPrompt} 
        onOpenChange={(open) => {
          if (!open) {
            // Check if mutations are pending
            const isPending = createCalendarMutation.isPending || createEventMutation.isPending;
            
            // Allow closing if:
            // 1. Mutations are not pending (normal case), OR
            // 2. Mutations have an error (error case - we want to close to show scope dialog)
            const hasError = createCalendarMutation.isError || createEventMutation.isError;
            
            if (!isPending || hasError) {
              setShowCalendarPrompt(false);
            }
            // If pending and no error, prevent closing (return without doing anything)
            return;
          }
          
          // Opening the dialog
          if (createCalendarMutation.isPending || createEventMutation.isPending) {
            return;
          }
          setShowCalendarPrompt(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Task-Fusion Calendar</DialogTitle>
            <DialogDescription>
              This will create a new Task-Fusion calendar. Do you want to proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={(e) => {
                e.stopPropagation();
                setShowCalendarPrompt(false);
              }}
              disabled={createCalendarMutation.isPending || createEventMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleCreateCalendarAndAdd();
              }}
              disabled={createCalendarMutation.isPending || createEventMutation.isPending}
            >
              {(createCalendarMutation.isPending || createEventMutation.isPending) ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                "Create and Add"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default TaskItem; 